import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { BookUser, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import * as Query from '../../../api/gql/query'
import { bidderOptions } from '../lib/rfq'
import type { CostBidderOption } from '../types'

const LIMIT = 100

interface Props { disabled?: boolean; onPick: (bidder: CostBidderOption) => void }

/**
 * "Z adresáře" (part D spec §6): the organization's bidder directory through the existing `bidders` query. A viewer
 * without access to the directory types the supplier by hand: the picker hides itself when the query fails.
 */
const BidderPicker: React.FC<Props> = ({ disabled, onPick }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const { data, error } = useQuery<{ bidders: CostBidderOption[] }>(Query.COST_BIDDER_OPTIONS, { fetchPolicy: 'cache-first', context: { handlesErrors: true } })
  const options = useMemo(() => (open ? bidderOptions(data?.bidders ?? [], query) : []), [open, data, query])
  if (error || !data || !data.bidders.some((b) => !b.deleted)) return null
  // modal: the picker lives inside the quote dialog, whose scroll lock would otherwise swallow the wheel over the list.
  return (
    <Popover modal open={open} onOpenChange={(next) => { setOpen(next); if (!next) setQuery('') }}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="shrink-0" disabled={disabled}>
          <BookUser className="h-4 w-4" />
          {t('CostControl_rfq_quote_from_directory')}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-2">
        <div className="relative mb-2">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('CostControl_rfq_quote_directory_search')} className="h-8 pl-8 text-sm" />
        </div>
        <ul className="max-h-72 overflow-y-auto">
          {options.slice(0, LIMIT).map((b) => (
            <li key={b.id}>
              <button type="button" className="w-full truncate rounded px-2 py-1.5 text-left text-sm hover:bg-accent" onClick={() => { onPick(b); setOpen(false); setQuery('') }}>
                {b.name}
              </button>
            </li>
          ))}
        </ul>
        {options.length === 0 && <p className="px-2 py-1.5 text-sm text-muted-foreground">{t('CostControl_rfq_quote_directory_empty')}</p>}
        {options.length > LIMIT && <p className="px-2 py-1.5 text-xs text-muted-foreground">{t('CostControl_rfq_quote_directory_more', { n: LIMIT })}</p>}
      </PopoverContent>
    </Popover>
  )
}
export default BidderPicker
